import { Fragment, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler } from '../api/client'
import type { Benutzer, Katalog, Systemparameter } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'

/**
 * Verwaltung fuer Administratoren: Benutzer anlegen und zuruecksetzen, Artikelnummern pflegen
 * (O-04) und die Rundungsregel umschalten, sobald der Kunde sie entschieden hat (R-07 / O-01).
 */
export default function Verwaltung() {
  const abfragen = useQueryClient()
  const [fehler, setFehler] = useState<string | null>(null)
  const [neuerName, setNeuerName] = useState('')
  const [neuesPasswort, setNeuesPasswort] = useState('')
  const [neuerAdmin, setNeuerAdmin] = useState(false)

  const ich = useQuery({ queryKey: ['ich'], queryFn: () => api.get<Benutzer>('/api/auth/me') })
  const istAdmin = ich.data?.istAdmin === true

  // Ohne Adminrecht werden die Daten gar nicht erst geholt - das Backend lehnt sie ohnehin ab.
  const benutzer = useQuery({
    queryKey: ['benutzer'],
    queryFn: () => api.get<Benutzer[]>('/api/benutzer'),
    enabled: istAdmin,
  })
  const katalog = useQuery({
    queryKey: ['katalog'],
    queryFn: () => api.get<Katalog>('/api/katalog'),
    enabled: istAdmin,
  })
  const parameter = useQuery({
    queryKey: ['systemparameter'],
    queryFn: () => api.get<Systemparameter[]>('/api/systemparameter'),
    enabled: istAdmin,
  })

  const melden = (ausnahme: unknown) =>
    setFehler(ausnahme instanceof ApiFehler ? ausnahme.message : 'Aktion nicht möglich.')

  const anlegen = useMutation({
    mutationFn: () =>
      api.post<Benutzer>('/api/benutzer', {
        benutzername: neuerName,
        passwort: neuesPasswort,
        istAdmin: neuerAdmin,
      }),
    onSuccess: () => {
      setNeuerName('')
      setNeuesPasswort('')
      setNeuerAdmin(false)
      setFehler(null)
      abfragen.invalidateQueries({ queryKey: ['benutzer'] })
    },
    onError: melden,
  })

  const umschalten = useMutation({
    mutationFn: (eintrag: Benutzer) =>
      api.patch(`/api/benutzer/${eintrag.id}`, { istAdmin: !eintrag.istAdmin }),
    onSuccess: () => abfragen.invalidateQueries({ queryKey: ['benutzer'] }),
    onError: melden,
  })

  const deaktivieren = useMutation({
    mutationFn: (eintrag: Benutzer) => api.patch(`/api/benutzer/${eintrag.id}`, { aktiv: !eintrag.aktiv }),
    onSuccess: () => abfragen.invalidateQueries({ queryKey: ['benutzer'] }),
    onError: melden,
  })

  const passwortSetzen = useMutation({
    mutationFn: ({ id, passwort }: { id: number; passwort: string }) =>
      api.put(`/api/benutzer/${id}/passwort`, { neuesPasswort: passwort }),
    onSuccess: () => setFehler(null),
    onError: melden,
  })

  const parameterSetzen = useMutation({
    mutationFn: ({ schluessel, wert }: { schluessel: string; wert: string }) =>
      api.put(`/api/systemparameter/${schluessel}`, { wert }),
    onSuccess: () => abfragen.invalidateQueries({ queryKey: ['systemparameter'] }),
    onError: melden,
  })

  const artikelnummerSetzen = useMutation({
    mutationFn: ({ pfad, nummer }: { pfad: string; nummer: string }) =>
      api.put(pfad, { artikelnummer: nummer }),
    onSuccess: () => abfragen.invalidateQueries({ queryKey: ['katalog'] }),
    onError: melden,
  })

  const rundungsregel = parameter.data?.find((eintrag) => eintrag.schluessel === 'rundungsregel')

  // Das Backend weist Fremdzugriffe ohnehin ab; die Oberfläche soll aber gar nicht erst
  // Eingabefelder zeigen, die zu einer Fehlermeldung führen würden.
  if (ich.isPending) {
    return (
      <AppRahmen titel="Verwaltung" zurueck="/">
        <div className="spinner" />
      </AppRahmen>
    )
  }

  if (!istAdmin) {
    return (
      <AppRahmen titel="Verwaltung" unter="Keine Berechtigung" zurueck="/">
        <p className="sek">
          Diese Seite ist Administratoren vorbehalten. Benutzer, Artikelnummern und die Rundungsregel
          verwaltet, wer Adminrechte hat.
        </p>
      </AppRahmen>
    )
  }

  return (
    <AppRahmen titel="Verwaltung" unter="Benutzer, Katalog und Parameter" zurueck="/" breit>
      {fehler && <p className="hinweiszeile">{fehler}</p>}

      <section className="gruppe">
        <h2 className="s">Benutzer</h2>

        {benutzer.data?.map((eintrag) => (
          <div key={eintrag.id} className="karte">
            <div className="zeile">
              <b>{eintrag.benutzername}</b>
              <span className={eintrag.aktiv ? 'st st-ok' : 'st st-warn'}>
                {eintrag.aktiv ? 'aktiv' : 'deaktiviert'}
              </span>
            </div>
            <span className="sek">{eintrag.istAdmin ? 'Administrator' : 'Anwender'}</span>
            <div className="wahl wahl-3">
              <button className="opt klein" onClick={() => umschalten.mutate(eintrag)}>
                {eintrag.istAdmin ? 'Adminrecht entziehen' : 'Zum Admin machen'}
              </button>
              <button className="opt klein" onClick={() => deaktivieren.mutate(eintrag)}>
                {eintrag.aktiv ? 'Deaktivieren' : 'Aktivieren'}
              </button>
              <button
                className="opt klein"
                onClick={() => {
                  const passwort = window.prompt(`Neues Passwort für ${eintrag.benutzername}`)
                  if (passwort) passwortSetzen.mutate({ id: eintrag.id, passwort })
                }}
              >
                Passwort setzen
              </button>
            </div>
          </div>
        ))}

        <div className="karte">
          <b>Neuen Benutzer anlegen</b>
          <div className="feld">
            <input
              type="text"
              placeholder="Benutzername"
              value={neuerName}
              onChange={(ereignis) => setNeuerName(ereignis.target.value)}
            />
          </div>
          <div className="feld">
            <input
              type="password"
              placeholder="Passwort (mindestens 8 Zeichen)"
              value={neuesPasswort}
              onChange={(ereignis) => setNeuesPasswort(ereignis.target.value)}
            />
          </div>
          <button
            className="opt klein"
            aria-pressed={neuerAdmin}
            onClick={() => setNeuerAdmin((wert) => !wert)}
          >
            {neuerAdmin ? 'Wird Administrator' : 'Normaler Anwender'}
          </button>
          <button
            className="btn btn-primaer"
            disabled={!neuerName || !neuesPasswort || anlegen.isPending}
            onClick={() => anlegen.mutate()}
          >
            Anlegen
          </button>
        </div>
      </section>

      <section className="gruppe">
        <h2 className="s">Rundungsregel</h2>
        <p className="sek">{rundungsregel?.beschreibung}</p>
        <div className="wahl wahl-2">
          {['AUFRUNDEN', 'ABRUNDEN'].map((wert) => (
            <button
              key={wert}
              className="opt"
              aria-pressed={rundungsregel?.wert === wert}
              onClick={() => parameterSetzen.mutate({ schluessel: 'rundungsregel', wert })}
            >
              {wert === 'AUFRUNDEN' ? 'nächstgrößere' : 'nächstkleinere'}
            </button>
          ))}
        </div>
      </section>

      <section className="gruppe">
        <h2 className="s">Artikelnummern</h2>
        <p className="sek">Solange neoalto die Nummern nicht geliefert hat, steht hier „OFFEN“.</p>

        <table className="liste">
          <thead>
            <tr>
              <th>Artikel</th>
              <th>Artikelnummer</th>
            </tr>
          </thead>
          <tbody>
            {/* Eine Smart Scale wird nie allein bestellt, sondern als Box H8, Box H16 oder ohne Box,
                dazu der FiFo-Einsatz je Höhe H8/H16 - je Artikel eine Kopfzeile, darunter die Varianten. */}
            {[
              ...(katalog.data?.boxen ?? []).map((box) => ({
                kennung: `box-${box.id}`,
                name: box.name,
                pfad: 'box-varianten',
                varianten: katalog.data!.boxVarianten.filter((variante) => variante.artikel === box.id),
              })),
              // FiFo-Einsätze gibt es nur in den Höhen der Boxen mit Korbtyp, nicht „ohne Box“.
              ...(katalog.data?.fifoEinsaetze ?? []).flatMap((einsatz) =>
                (katalog.data?.boxen ?? [])
                  .filter((box) => box.korbtyp !== null)
                  .map((box) => ({
                    kennung: `fifo-${einsatz.id}-${box.id}`,
                    name: `${einsatz.name} ${box.id}`,
                    pfad: 'fifo-varianten',
                    varianten: katalog.data!.fifoVarianten.filter(
                      (variante) => variante.artikel === einsatz.id && variante.box === box.id,
                    ),
                  })),
              ),
            ].map((artikel) => (
              <Fragment key={artikel.kennung}>
                <tr className="zwischenkopf">
                  <td colSpan={2}>{artikel.name}</td>
                </tr>
                {artikel.varianten.map((variante) => (
                  <tr key={variante.id} className="variante">
                    <td>{variante.name}</td>
                    <td>
                      <ArtikelnummerFeld
                        artikel={variante.name}
                        wert={variante.artikelnummer}
                        speichern={(nummer) =>
                          artikelnummerSetzen.mutate({
                            pfad: `/api/katalog/${artikel.pfad}/${variante.id}/artikelnummer`,
                            nummer,
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </section>
    </AppRahmen>
  )
}

/**
 * Artikelnummer direkt in der Zeile. Gespeichert wird beim Verlassen des Felds oder mit Enter,
 * und nur, wenn sich etwas geaendert hat. Solange nichts getippt wurde, folgt das Feld dem
 * Serverstand - eine einmalige Vorbelegung wuerde den ersten zwischengespeicherten Stand einfrieren.
 */
function ArtikelnummerFeld({
  artikel,
  wert,
  speichern,
}: {
  artikel: string
  wert: string
  speichern: (nummer: string) => void
}) {
  const [entwurf, setEntwurf] = useState<string | null>(null)
  const angezeigt = entwurf ?? wert

  // Der Entwurf bleibt bis zum neuen Serverstand stehen; sonst blitzte nach dem Speichern kurz die
  // alte Nummer auf. Scheitert das Speichern, bleibt die Eingabe fuer einen zweiten Versuch.
  useEffect(() => setEntwurf(null), [wert])

  const uebernehmen = () => {
    if (entwurf === null) return
    const nummer = entwurf.trim()
    if (nummer === '' || nummer === wert) {
      setEntwurf(null)
      return
    }
    speichern(nummer)
  }

  return (
    <input
      type="text"
      className="artikelnummer"
      aria-label={`Artikelnummer für ${artikel}`}
      value={angezeigt}
      onChange={(ereignis) => setEntwurf(ereignis.target.value)}
      onBlur={uebernehmen}
      onKeyDown={(ereignis) => {
        if (ereignis.key === 'Enter') ereignis.currentTarget.blur()
      }}
    />
  )
}
